import * as React from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, cn, toast } from "@eduviaos/ui-kit";
import { ArrowRight, CalendarClock, Mail, Phone, UserRound } from "lucide-react";
import type {
  AdmissionEnquiry,
  AdmissionFunnelResponse,
  AdmissionFunnelStage,
} from "@eduviaos/shared";
import { admissionKeys } from "@/hooks/useAdmissions";
import { api } from "@/lib/api";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { getVerticalProfile } from "@/lib/verticalProfile";

/**
 * Phase D3 — Admissions Kanban with drag-and-drop.
 * Phase E1.b.3a.3 — error surface migrated from inline banner → global toast.
 *
 * Replaces the read-only board with a fully interactive funnel:
 *
 *   - Pointer + touch + keyboard sensors (via @dnd-kit) — meets WCAG
 *     AA "operable by keyboard" because every card stays a real
 *     `<Link>` for click-to-detail; drag-and-drop is layered on top.
 *   - Optimistic mutation: card hops to the target column on drop,
 *     then `POST /advance` fires. On 4xx (e.g. INVALID_TRANSITION
 *     because someone changed the funnel under us) we rollback the
 *     move and surface a `toast.error` with the BE message + the
 *     reverted-to-stage label. The visual rollback (card animating
 *     back to its source column) is the primary discovery surface;
 *     the toast is supplementary, explaining WHY the rollback fired.
 *     This composes correctly with the Phase E1.b decision rule:
 *     "Page-level error where the originating UI closes on settle →
 *     global toast" — a drag-drop completes on drop, and there is no
 *     inline anchor (no per-field error markers, no destructive
 *     dialog still open) the toast would orphan.
 *   - Drag overlay: the picked card floats with cursor; the source
 *     position renders an `opacity-40` "ghost" so the IA never loses
 *     spatial context.
 *   - Terminal stages (admitted / rejected) remain DROP-DISABLED. The
 *     IA must use the per-row "Convert" / "Reject" dialogs — those
 *     paths capture the required metadata that a naked stage advance
 *     can't infer (admission_number, rejection_reason). The defensive
 *     branch in `onDragEnd` that catches a leak through @dnd-kit's
 *     over-resolution still toasts (rather than silently advancing).
 *
 * Column order, labels, and color hints continue to come from the BE
 * funnel config so a Super Admin re-ordering stages reflects on the
 * IA board within the funnel staleTime (60s).
 */

interface AdmissionsBoardProps {
  funnel: AdmissionFunnelResponse;
  enquiries: AdmissionEnquiry[];
}

function resolveColorHint(hint: string | null): { dot: string; ring: string } {
  if (!hint) return { dot: "bg-muted-foreground/40", ring: "ring-border/60" };
  if (hint.startsWith("#")) {
    return { dot: "", ring: "ring-border/60" };
  }
  const map: Record<string, { dot: string; ring: string }> = {
    "tokens.color.success": { dot: "bg-emerald-500", ring: "ring-emerald-500/30" },
    "tokens.color.warning": { dot: "bg-amber-500", ring: "ring-amber-500/30" },
    "tokens.color.danger": { dot: "bg-rose-500", ring: "ring-rose-500/30" },
    "tokens.color.info": { dot: "bg-sky-500", ring: "ring-sky-500/30" },
    "tokens.color.primary": { dot: "bg-primary", ring: "ring-primary/30" },
  };
  return map[hint] ?? { dot: "bg-muted-foreground/40", ring: "ring-border/60" };
}

function inlineDotStyle(hint: string | null): React.CSSProperties | undefined {
  if (hint && hint.startsWith("#")) return { backgroundColor: hint };
  return undefined;
}

interface FollowupHint {
  label: string;
  tone: "overdue" | "today" | "soon" | "scheduled";
}

/** Compress a follow-up datetime into a 1-line "due" hint. */
function followupHint(iso: string | null): FollowupHint | null {
  if (!iso) return null;
  const at = new Date(iso);
  const now = new Date();
  const diffMs = at.getTime() - now.getTime();
  const dayMs = 86_400_000;

  if (diffMs < 0) {
    return {
      label: `Overdue · ${at.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`,
      tone: "overdue",
    };
  }
  // Today (any time still in the same calendar day, local timezone).
  if (
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  ) {
    return {
      label: `Today · ${at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`,
      tone: "today",
    };
  }
  if (diffMs <= dayMs * 7) {
    return {
      label: at.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      tone: "soon",
    };
  }
  return {
    label: at.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    tone: "scheduled",
  };
}

const followupToneClasses: Record<FollowupHint["tone"], string> = {
  overdue: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  today: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  soon: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  scheduled: "bg-muted/60 text-muted-foreground border-border/60",
};

interface CachedListShape {
  items: AdmissionEnquiry[];
  total: number;
  limit: number;
  offset: number;
}

export function AdmissionsBoard({ funnel, enquiries }: AdmissionsBoardProps) {
  const qc = useQueryClient();
  const [activeEnquiry, setActiveEnquiry] = React.useState<AdmissionEnquiry | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const sortedStages = React.useMemo(
    () => [...funnel.stages].sort((a, b) => a.order - b.order),
    [funnel.stages],
  );
  const liveStages = sortedStages.filter((s) => !s.is_terminal);
  const terminalStages = sortedStages.filter((s) => s.is_terminal);

  // Local "in-flight optimistic" override map keyed by enquiry id →
  // pending stage_key. This lets the dropped card display in its new
  // column immediately, even while the BE call hasn't settled.
  const [optimisticStage, setOptimisticStage] = React.useState<Record<string, string>>({});

  const enquiriesView = React.useMemo(() => {
    if (!Object.keys(optimisticStage).length) return enquiries;
    return enquiries.map((e) =>
      optimisticStage[e.id] ? { ...e, stage_key: optimisticStage[e.id]! } : e,
    );
  }, [enquiries, optimisticStage]);

  const byStage = React.useMemo(() => {
    const map = new Map<string, AdmissionEnquiry[]>();
    for (const e of enquiriesView) {
      const list = map.get(e.stage_key) ?? [];
      list.push(e);
      map.set(e.stage_key, list);
    }
    return map;
  }, [enquiriesView]);

  const onDragStart = (e: DragStartEvent) => {
    const id = e.active.id as string;
    const enq = enquiriesView.find((x) => x.id === id) ?? null;
    setActiveEnquiry(enq);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveEnquiry(null);
    if (!e.over) return;
    const enquiryId = e.active.id as string;
    const targetStage = e.over.id as string;
    const enq = enquiriesView.find((x) => x.id === enquiryId);
    if (!enq) return;
    if (enq.stage_key === targetStage) return; // no-op drop on self
    const target = sortedStages.find((s) => s.stage_key === targetStage);
    if (!target || target.is_terminal) {
      // Defensive — terminal columns are not registered as droppable
      // but if @dnd-kit ever changes its over-resolution we still want
      // to fail loudly instead of silently advancing past convert/
      // reject business rules.
      toast.error(
        `Use the row's "Convert" or "Reject" action to move into ${target?.label ?? "this stage"}.`,
      );
      return;
    }

    const previousStage = enq.stage_key;
    setOptimisticStage((prev) => ({ ...prev, [enquiryId]: targetStage }));

    try {
      // Fire the advance call — fire-and-await so the catch block
      // runs on 4xx. Cache invalidation lives inside `advanceForId`
      // so the canonical row replaces the optimistic override.
      await advanceForId(qc, enquiryId, { to_stage_key: targetStage });
      // On success, drop the optimistic override; the invalidated
      // cache delivers the authoritative row on the next render.
      setOptimisticStage((prev) => {
        const next = { ...prev };
        delete next[enquiryId];
        return next;
      });
    } catch (err) {
      // Rollback the optimistic move (visual: card returns to its
      // source column — that is the primary discovery surface for
      // the rejection) and surface a toast carrying the BE reason.
      setOptimisticStage((prev) => {
        const next = { ...prev };
        delete next[enquiryId];
        return next;
      });
      // Force the cache to reflect the original state in case the
      // user already started another drag.
      qc.setQueryData<CachedListShape | undefined>(
        admissionKeys.enquiries(),
        (old) => old, // no-op; main list is authoritative
      );
      const beMessage =
        (err as { message?: string; code?: string } | null)?.message ??
        "Couldn't move the card — refresh and try again.";
      toast.error(
        `${enq.student_name}: ${beMessage}. Reverted to ${previousStageLabel(sortedStages, previousStage)}.`,
      );
    }
  };

  return (
    <div className="space-y-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveEnquiry(null)}
      >
        <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
          <div className="border-border/60 bg-card flex min-h-[480px] gap-4 overflow-x-auto rounded-lg border p-4">
            {liveStages.map((stage) => {
              const items = byStage.get(stage.stage_key) ?? [];
              const colors = resolveColorHint(stage.color_hint);
              return (
                <DroppableColumn
                  key={stage.stage_key}
                  stage={stage}
                  count={items.length}
                  colors={colors}
                  dotStyle={inlineDotStyle(stage.color_hint)}
                >
                  {items.length === 0 ? (
                    <EmptyColumn label={stage.label} />
                  ) : (
                    items.map((enq) => (
                      <DraggableCard
                        key={enq.id}
                        enquiry={enq}
                        nextStageLabel={liveStages[liveStages.indexOf(stage) + 1]?.label}
                      />
                    ))
                  )}
                </DroppableColumn>
              );
            })}
          </div>

          {terminalStages.length > 0 && (
            <div className="grid gap-3">
              {terminalStages.map((stage) => {
                const items = byStage.get(stage.stage_key) ?? [];
                const isAdmitted = stage.terminal_outcome === "admitted";
                return (
                  <div
                    key={stage.stage_key}
                    className={cn(
                      "border-border/60 bg-card min-w-[240px] rounded-lg border p-4",
                      "lg:w-[260px]",
                    )}
                  >
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex size-2 rounded-full",
                            isAdmitted ? "bg-emerald-500" : "bg-rose-500",
                          )}
                        />
                        <span className="text-foreground text-sm font-semibold">{stage.label}</span>
                      </div>
                      <Badge variant="secondary" className="tabular-nums">
                        {items.length}
                      </Badge>
                    </div>
                    {items.length === 0 ? (
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {isAdmitted
                          ? "No conversions yet. Convert an enquiry from its detail view."
                          : "No rejections — keep that ratio healthy."}
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {items.slice(0, 5).map((enq) => (
                          <li key={enq.id}>
                            <Link
                              to={`/admissions/${enq.id}`}
                              className={cn(
                                "border-border/40 bg-background hover:border-border block rounded-md border px-2.5 py-2 text-xs transition-colors",
                              )}
                            >
                              <div className="text-foreground truncate font-medium">
                                {enq.student_name}
                              </div>
                              <div className="text-muted-foreground mt-0.5 truncate text-[11px]">
                                {enq.last_advanced_at
                                  ? new Date(enq.last_advanced_at).toLocaleDateString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "—"}
                              </div>
                            </Link>
                          </li>
                        ))}
                        {items.length > 5 && (
                          <li className="text-muted-foreground pt-1 text-[11px]">
                            + {items.length - 5} more
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeEnquiry ? (
            <div className="rotate-1">
              <CardContent enquiry={activeEnquiry} dragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function previousStageLabel(stages: AdmissionFunnelStage[], key: string): string {
  return stages.find((s) => s.stage_key === key)?.label ?? key;
}

// Internal helper that calls the advance endpoint without binding to
// a specific id at hook-creation time (which would be wrong here —
// the dragged enquiry id is only known on drop). Mirrors the cache
// invalidation contract `useAdvanceEnquiry` would have applied.
async function advanceForId(
  qc: ReturnType<typeof useQueryClient>,
  enquiryId: string,
  payload: { to_stage_key: string; note?: string; payload?: Record<string, unknown> },
): Promise<void> {
  const data = await api.post<AdmissionEnquiry>(
    `/admissions/enquiries/${enquiryId}/advance`,
    payload,
  );
  qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
  await qc.invalidateQueries({ queryKey: admissionKeys.all });
}

// ── Droppable column ────────────────────────────────────────────────────

interface DroppableColumnProps {
  stage: AdmissionFunnelStage;
  count: number;
  colors: { dot: string; ring: string };
  dotStyle?: React.CSSProperties;
  children: React.ReactNode;
}

function DroppableColumn({ stage, count, colors, dotStyle, children }: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.stage_key });
  return (
    <section
      ref={setNodeRef}
      className={cn(
        "bg-muted/30 flex w-[300px] shrink-0 flex-col rounded-md p-3 ring-1 transition-colors",
        colors.ring,
        isOver && "bg-primary/10 ring-primary/40",
      )}
      aria-label={`${stage.label} stage column — drop target`}
      data-testid={`board-column-${stage.stage_key}`}
    >
      <header className="mb-3 flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <span
            className={cn("inline-flex size-2.5 rounded-full", colors.dot)}
            style={dotStyle}
            aria-hidden="true"
          />
          <h3 className="text-foreground text-sm font-semibold">{stage.label}</h3>
        </div>
        <Badge variant="secondary" className="text-[10px] tabular-nums">
          {count}
        </Badge>
      </header>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto pr-1">{children}</div>
    </section>
  );
}

function EmptyColumn({ label }: { label: string }) {
  return (
    <div className="border-border/40 text-muted-foreground/70 flex min-h-[120px] flex-col items-center justify-center rounded-md border border-dashed p-4 text-center text-xs">
      <span className="leading-relaxed">
        Drop here or no enquiries in <span className="text-foreground/80 font-medium">{label}</span>{" "}
        yet.
      </span>
    </div>
  );
}

// ── Draggable card ──────────────────────────────────────────────────────

interface DraggableCardProps {
  enquiry: AdmissionEnquiry;
  nextStageLabel?: string;
}

function DraggableCard({ enquiry, nextStageLabel }: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: enquiry.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn("relative", isDragging && "opacity-40")}
      data-testid={`enquiry-card-${enquiry.id}`}
    >
      <CardContent enquiry={enquiry} nextStageLabel={nextStageLabel} />
    </div>
  );
}

interface CardContentProps {
  enquiry: AdmissionEnquiry;
  nextStageLabel?: string;
  dragging?: boolean;
}

function CardContent({ enquiry, nextStageLabel, dragging }: CardContentProps) {
  const followup = followupHint(enquiry.next_followup_at);
  const linkProps = dragging ? { onClick: (e: React.MouseEvent) => e.preventDefault() } : {};
  // SC Foundation (F0d) — a study centre has no parent/guardian, so the
  // card drops the parent line entirely. School / coaching still show it.
  const { vertical } = useEnabledModules();
  const showParent = getVerticalProfile(vertical ?? "school").has.parents;

  return (
    <Link
      to={`/admissions/${enquiry.id}`}
      className={cn(
        "border-border/60 bg-card group block rounded-md border p-3 text-left",
        "hover:border-primary/40 hover:bg-card/95 transition-colors",
        dragging && "ring-primary/30 cursor-grabbing shadow-xl ring-2",
      )}
      {...linkProps}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-foreground truncate text-sm font-medium">{enquiry.student_name}</div>
          {showParent && enquiry.parent_name && (
            <div className="text-muted-foreground mt-0.5 flex items-center gap-1 truncate text-[11px]">
              <UserRound className="size-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{enquiry.parent_name}</span>
            </div>
          )}
        </div>
        {enquiry.source && (
          <Badge variant="outline" className="shrink-0 text-[10px] font-normal">
            {enquiry.source.replace(/_/g, " ")}
          </Badge>
        )}
      </div>

      {(enquiry.student_email || enquiry.student_phone) && (
        <div className="text-muted-foreground mt-2 space-y-0.5 text-[11px]">
          {enquiry.student_email && (
            <div className="flex items-center gap-1.5">
              <Mail className="size-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{enquiry.student_email}</span>
            </div>
          )}
          {enquiry.student_phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="size-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{enquiry.student_phone}</span>
            </div>
          )}
        </div>
      )}

      {(followup || enquiry.collaborators.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {followup && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                followupToneClasses[followup.tone],
              )}
            >
              <CalendarClock className="size-3 shrink-0" aria-hidden="true" />
              {followup.label}
            </span>
          )}
          {enquiry.collaborators.length > 0 && (
            <span className="bg-muted/60 text-muted-foreground border-border/60 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]">
              <UserRound className="size-3" aria-hidden="true" />
              {enquiry.collaborators.length}
            </span>
          )}
        </div>
      )}

      <div className="border-border/40 mt-2.5 flex items-center justify-between gap-2 border-t pt-2">
        <span className="text-muted-foreground text-[10px] tabular-nums">
          {enquiry.last_advanced_at
            ? new Date(enquiry.last_advanced_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            : new Date(enquiry.created_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
        </span>
        {nextStageLabel && (
          <span className="text-muted-foreground/80 group-hover:text-primary flex items-center gap-1 text-[10px] transition-colors">
            <span>Next: {nextStageLabel}</span>
            <ArrowRight className="size-3" aria-hidden="true" />
          </span>
        )}
      </div>
    </Link>
  );
}
